import type { DiffChange } from "@gaurussel/tiptap-diff-utility";
import type { Node } from "prosemirror-model";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

interface InsertedTextState {
	changes: DiffChange[] | undefined;
	set: DecorationSet;
}

export const insertedTextKey = new PluginKey<InsertedTextState>("diff-inserted-text");

const isCurrent = (change: DiffChange, doc: Node) => {
	const pos = change.posB as number;
	if (pos < 0 || pos + change.nodeB.nodeSize > doc.content.size) return false;
	return doc.nodeAt(pos)?.eq(change.nodeB as Node) ?? false;
};

const decorationsFor = (changes: DiffChange[] | undefined, doc: Node) => {
	const decorations = (changes ?? [])
		.filter((change) => change.type === "inserted" && change.posB !== undefined && change.nodeB)
		.filter((change) => isCurrent(change, doc))
		.map((change) => {
			const from = (change.posB as number) + 1;
			const to = (change.posB as number) + change.nodeB.nodeSize - 1;
			// ProseMirror rejects zero-length inline decorations.
			if (to <= from) return null;
			return Decoration.inline(from, to, { class: "diff-inline-inserted" });
		})
		.filter(Boolean);
	return decorations.length ? DecorationSet.create(doc, decorations) : DecorationSet.empty;
};

const insertedText = (getChanges: () => DiffChange[] | undefined) =>
	new Plugin<InsertedTextState>({
		key: insertedTextKey,
		state: {
			init: (_, state) => ({ changes: getChanges(), set: decorationsFor(getChanges(), state.doc) }),
			apply: (tr, value) => {
				if (!tr.getMeta(insertedTextKey)) return { ...value, set: value.set.map(tr.mapping, tr.doc) };
				const changes = getChanges();
				return { changes, set: decorationsFor(changes, tr.doc) };
			},
		},
		// The library swaps `storage.diff.changes` inside its own plugin's apply, which may run after this one.
		appendTransaction: (_, __, state) =>
			getChanges() === insertedTextKey.getState(state)?.changes
				? null
				: state.tr.setMeta(insertedTextKey, true).setMeta("addToHistory", false),
		props: {
			decorations(state) {
				const value = insertedTextKey.getState(state);
				// Before the first transaction the library has filled the storage for this very doc.
				if (value && getChanges() !== value.changes) return decorationsFor(getChanges(), state.doc);
				return value?.set;
			},
		},
	});

export default insertedText;
